import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { ccoreApi } from "@/lib/ccore";
import { Panel } from "@/components/common/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Mail, RotateCcw, Ban, Copy, Check } from "lucide-react";

const STATUS_TONE = {
  PENDING: "bg-copper-light text-copper", ACCEPTED: "bg-emerald-50 text-emerald-700",
  REVOKED: "bg-ink/5 text-ink-muted", REJECTED: "bg-ink/5 text-ink-muted", EXPIRED: "bg-ink/5 text-ink-muted",
};

export default function OrgMembersPanel() {
  const { ccore, ccoreCan } = useApp();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [roleCode, setRoleCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const canInvite = ccoreCan("org.member.invite");
  const capSet = new Set(ccore?.capabilities || []);
  const grantableRoles = roles.filter((r) => r.scope === "CORE" && (r.capabilities || []).every((c) => capSet.has(c)));

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [m, inv, rl] = await Promise.all([ccoreApi.members(), ccoreApi.orgInvites(), ccoreApi.orgRoles()]);
      setMembers(m); setInvites(inv); setRoles(rl);
    } catch (e) {
      setError(e?.response?.data?.detail || "We couldn't load your organisation's members and invitations. Please check your connection and try again.");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (ccore?.ready && ccore.orgId) load(); /* eslint-disable-next-line */ }, [ccore?.orgId, ccore?.ready]);

  const linkFor = (token) => `${window.location.origin}/invite/${token}`;
  const copy = async (link) => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1800); toast.success("Invite link copied"); }
    catch (e) { toast.error("Could not copy. Select and copy manually."); }
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error("Enter a valid email"); return; }
    if (!roleCode) { toast.error("Choose a role"); return; }
    setBusy(true);
    try {
      const res = await ccoreApi.createInvite({ email: email.trim().toLowerCase(), roleCode });
      setLastLink(linkFor(res.token));
      toast.success("Invitation created. Share the link below.");
      setEmail(""); setRoleCode("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create invitation");
    } finally { setBusy(false); }
  };

  const revoke = async (id) => { try { await ccoreApi.revokeInvite(id); toast.success("Invitation revoked"); await load(); } catch (e) { toast.error("Could not revoke"); } };
  const resend = async (id) => { try { const res = await ccoreApi.resendInvite(id); setLastLink(linkFor(res.token)); toast.success("New link generated (old link is now invalid)"); await load(); } catch (e) { toast.error("Could not resend"); } };

  if (!ccore?.ready || !ccore.orgId) return <Panel className="p-5"><p className="text-sm text-ink-2">Organisation context is still loading.</p></Panel>;

  return (
    <div className="space-y-4" data-testid="org-members-panel">
      {error && (
        <Panel className="border-danger/30 p-4" data-testid="org-members-error">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-danger">{error}</p>
            <Button size="sm" variant="outline" onClick={load} data-testid="org-members-retry">Retry</Button>
          </div>
        </Panel>
      )}
      {canInvite && (
        <Panel className="p-5">
          <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-copper" /><h3 className="font-display text-lg font-semibold text-ink">Invite a member</h3></div>
          <p className="mt-1 text-sm text-ink-2">Email delivery is disabled — share the generated single-use link. Only the person with the matching account email can accept.</p>
          <form onSubmit={submitInvite} className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end" data-testid="invite-form">
            <div className="flex-1">
              <label className="text-xs font-medium text-ink-2">Email</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="colleague@example.com" data-testid="invite-email" />
            </div>
            <div className="w-full sm:w-52">
              <label className="text-xs font-medium text-ink-2">Role</label>
              <Select value={roleCode} onValueChange={setRoleCode}>
                <SelectTrigger data-testid="invite-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {grantableRoles.map((r) => <SelectItem key={r.code} value={r.code} data-testid={`invite-role-${r.code}`}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={busy} data-testid="invite-submit">Generate invitation link</Button>
          </form>
          {lastLink && (
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-line bg-card p-2" data-testid="invite-link-box">
              <code className="flex-1 truncate text-xs text-ink-2">{lastLink}</code>
              <Button size="sm" variant="outline" onClick={() => copy(lastLink)} data-testid="invite-link-copy">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button>
            </div>
          )}
        </Panel>
      )}

      <Panel className="p-5">
        <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-ink-2" /><h3 className="font-display text-lg font-semibold text-ink">Invitations</h3></div>
        {loading ? <p className="mt-3 text-sm text-ink-muted">Loading…</p> : invites.length === 0 ? (
          <p className="mt-3 text-sm text-ink-muted" data-testid="invites-empty">No invitations yet.</p>
        ) : (
          <div className="mt-3 divide-y divide-line" data-testid="invites-list">
            {invites.map((i) => (
              <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`invite-row-${i.id}`}>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-ink">{i.email}</div>
                  <div className="text-[11px] text-ink-muted">{i.roleCode} · expires {String(i.expiresAt).slice(0, 10)}{i.resendCount ? ` · resent ${i.resendCount}×` : ""}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATUS_TONE[i.status] || "bg-ink/5 text-ink-muted"}`}>{i.status}</span>
                  {canInvite && i.status === "PENDING" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => resend(i.id)} data-testid={`invite-resend-${i.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="outline" onClick={() => revoke(i.id)} data-testid={`invite-revoke-${i.id}`}><Ban className="h-3.5 w-3.5" /></Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel className="p-5">
        <div className="flex items-center gap-2"><Users className="h-4 w-4 text-ink-2" /><h3 className="font-display text-lg font-semibold text-ink">Members</h3></div>
        {loading ? <p className="mt-3 text-sm text-ink-muted">Loading…</p> : (
          <div className="mt-3 divide-y divide-line" data-testid="members-list">
            {members.map((m) => (
              <div key={m.membershipId} className="flex items-center justify-between py-2.5" data-testid={`member-row-${m.membershipId}`}>
                <div><div className="text-sm font-medium text-ink">{m.name || m.email}</div><div className="text-[11px] text-ink-muted">{m.email}</div></div>
                <div className="text-xs text-ink-2">{(m.roles || []).join(", ") || "member"} · {m.status}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
